 
/**
 * S05 · Annotate photo — canvas overlay on a freshly captured photo.
 *
 * Four tools, all four of them already in the schema. `CaptureAnnotation` has
 * carried `kind: 'circle' | 'arrow' | 'label' | 'freehand'` and a free-form
 * hex `color` since it was written; the SQLite column, the sync wire and the
 * replay path round-trip every one of them. This screen was the only thing
 * that hardcoded freehand and a single colour, which is why enabling the rest
 * needed no migration and no server change.
 *
 * Every tool stores normalised [0,1] `points`, so annotations survive
 * arbitrary image resizing on replay:
 *
 *   freehand  every sampled point along the drag
 *   circle    [centre, edge] — drawn as an ellipse, see `AnnotationShape`
 *   arrow     [tail, head]
 *   label     [anchor], with the text in `label`
 *
 * Save merges the marks into the capture row.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { CaptureAnnotation } from '@kapture/types';
import {
  Btn,
  Chip,
  GestureBar,
  I,
  Ico,
  ScreenFrame,
  StatusBar,
  T,
} from '@/components/primitives';
import { useCapture, useUpdateAnnotationsMutation } from '@kapture/client-core';
import { useBlobUrl } from '@/lib/device/useBlobUrl';

export const Route = createFileRoute('/sessions/$sessionId/capture/photo/review/$captureId')({
  component: AnnotatePhotoPage,
  // One line about the frame that was just taken — out of focus, very dark,
  // the same shot as the last one. Measured at the shutter from the live
  // frame, and carried here rather than recomputed: this screen has the stored
  // JPEG, and re-measuring a re-encoded image answers a slightly different
  // question than the one asked when the subject was still in front of the
  // camera. Optional, and absent whenever there is nothing to say.
  validateSearch: (search: Record<string, unknown>): { advice?: string } =>
    typeof search['advice'] === 'string' ? { advice: search['advice'] } : {},
  staticData: { mobile: true, fullscreen: true },
});

type Point = [number, number];
type Tool = CaptureAnnotation['kind'];

interface ActiveStroke {
  id: string;
  kind: Tool;
  points: Point[];
  color: string;
}

/**
 * A drag shorter than this is a tap that slipped, not a shape. Freehand is
 * exempt — it has its own two-point floor, and a short scribble is still a
 * scribble the inspector meant.
 */
const MIN_DRAG = 0.01;

/** Brand first, then the three that read on a dark or busy photograph. */
const PALETTE: readonly string[] = [T.brand, '#ffff00', '#ff0000', '#ffffff'];

const TOOL_LABELS: Record<Tool, string> = {
  freehand: 'Draw',
  circle: 'Circle',
  arrow: 'Arrow',
  label: 'Label',
};

function AnnotatePhotoPage() {
  const { sessionId, captureId } = Route.useParams();
  const { advice } = Route.useSearch();
  const [adviceDismissed, setAdviceDismissed] = useState(false);
  const navigate = useNavigate();
  const { data: capture } = useCapture(captureId);
  const updateAnnotations = useUpdateAnnotationsMutation();

  const blobPath = capture?.kind === 'photo' ? capture.blobPath : null;
  const url = useBlobUrl(blobPath);
  const existing = useMemo<CaptureAnnotation[]>(
    () => (capture?.kind === 'photo' ? capture.annotations : []),
    [capture],
  );

  const [strokes, setStrokes] = useState<CaptureAnnotation[]>([]);
  const [active, setActive] = useState<ActiveStroke | null>(null);
  const [tool, setTool] = useState<Tool>('freehand');
  const [color, setColor] = useState<string>(T.brand);
  // The label tool places its anchor on tap, then waits for text. Holding the
  // anchor here rather than committing an empty annotation means a cancelled
  // label leaves nothing behind.
  const [pendingLabel, setPendingLabel] = useState<{ id: string; point: Point; color: string } | null>(
    null,
  );
  const [labelText, setLabelText] = useState('');
  const surfaceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setStrokes(existing);
  }, [existing]);

  if (capture && capture.kind !== 'photo') {
    return (
      <ScreenFrame tone="light">
        <StatusBar />
        <div
          style={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            color: T.textDim,
            fontFamily: T.font,
            padding: 24,
          }}
        >
          That capture isn't a photo.
        </div>
      </ScreenFrame>
    );
  }

  function pointerToNormalized(e: React.PointerEvent): Point | null {
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return [Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y))];
  }

  function onPointerDown(e: React.PointerEvent) {
    const p = pointerToNormalized(e);
    if (!p) return;
    if (tool === 'label') {
      setPendingLabel({ id: crypto.randomUUID(), point: p, color });
      setLabelText('');
      return;
    }
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setActive({ id: crypto.randomUUID(), kind: tool, points: [p], color });
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!active) return;
    const p = pointerToNormalized(e);
    if (!p) return;
    // Freehand accumulates; the two-point shapes rubber-band from their origin.
    setActive(
      active.kind === 'freehand'
        ? { ...active, points: [...active.points, p] }
        : { ...active, points: [active.points[0]!, p] },
    );
  }

  function onPointerUp() {
    if (!active) return;
    const committed = toAnnotation(active);
    if (committed) setStrokes((prev) => [...prev, committed]);
    setActive(null);
  }

  function commitLabel() {
    const text = labelText.trim();
    if (!pendingLabel || !text) return;
    setStrokes((prev) => [
      ...prev,
      {
        id: pendingLabel.id,
        kind: 'label',
        points: [pendingLabel.point],
        color: pendingLabel.color,
        // The schema caps this at 120; trim here so a long caption is short
        // rather than a parse failure on the way into SQLite.
        label: text.slice(0, 120),
      },
    ]);
    setPendingLabel(null);
    setLabelText('');
  }

  function cancelLabel() {
    setPendingLabel(null);
    setLabelText('');
  }

  function undo() {
    if (active) return;
    setStrokes((prev) => prev.slice(0, -1));
  }

  function clear() {
    setStrokes([]);
    setActive(null);
    cancelLabel();
  }

  async function save() {
    await updateAnnotations.mutateAsync({ captureId, annotations: strokes });
    navigate({
      to: '/sessions/$sessionId/captures/$captureId',
      params: { sessionId, captureId },
    });
  }

  function discard() {
    navigate({
      to: '/sessions/$sessionId/captures/$captureId',
      params: { sessionId, captureId },
    });
  }

  // The pending label is not counted: it has no text yet, so it is not a mark
  // the inspector could save.
  const count = strokes.length + (active ? 1 : 0);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: T.hudBg,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: T.font,
      }}
    >
      <StatusBar dark />
      {advice && !adviceDismissed ? (
        // Advisory, never a block. The inspector was there and the camera was
        // not; a measurement that overrides them would be wrong the first time
        // it met backlight or a wet surface, and a warning people learn to
        // distrust is worse than no warning at all.
        <div
          role="status"
          style={{
            margin: '8px 12px 0',
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            borderRadius: 10,
            background: 'rgba(255,196,0,0.14)',
            border: '1px solid rgba(255,196,0,0.45)',
            fontSize: 12.5,
            lineHeight: 1.45,
          }}
        >
          <span style={{ flex: 1 }}>{advice}</span>
          <button
            type="button"
            onClick={() => setAdviceDismissed(true)}
            aria-label="Dismiss"
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              opacity: 0.75,
              cursor: 'pointer',
              padding: 0,
              fontSize: 12.5,
              fontWeight: 700,
            }}
          >
            OK
          </button>
        </div>
      ) : null}
      <div
        style={{
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <button
          type="button"
          onClick={discard}
          aria-label="Discard annotations"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: '#fff',
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontSize: 13,
            fontFamily: T.font,
            fontWeight: 600,
          }}
        >
          <Ico d={I.close} size={18} />
          Discard
        </button>
        <div
          style={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.5)',
            fontFamily: T.mono,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {count} {count === 1 ? 'mark' : 'marks'}
        </div>
        <Btn
          tone="primary"
          size="sm"
          onClick={() => void save()}
          disabled={updateAnnotations.isPending}
          style={{ opacity: updateAnnotations.isPending ? 0.5 : 1 }}
        >
          {updateAnnotations.isPending ? 'Saving…' : 'Save'}
        </Btn>
      </div>

      <div
        style={{
          position: 'relative',
          margin: '6px 12px',
          borderRadius: 12,
          overflow: 'hidden',
          flex: 1,
          background: '#111',
          touchAction: 'none',
        }}
        ref={surfaceRef}
        // The drawing surface is the one element here with no accessible name
        // of its own, and every icon on the screen is also an <svg> — so a
        // test reaching for the canvas by tag picks a status-bar glyph and
        // silently drags nowhere.
        data-testid="annotate-surface"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {url ? (
          <img
            src={url}
            alt="Captured photo"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
            draggable={false}
          />
        ) : (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              color: 'rgba(255,255,255,0.5)',
              fontFamily: T.mono,
              fontSize: 12,
            }}
          >
            Loading photo…
          </div>
        )}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        >
          {strokes.map((s) => (
            <AnnotationShape key={s.id} a={s} />
          ))}
          {active && <AnnotationShape a={{ ...active, points: active.points }} preview />}
          {pendingLabel && (
            // The anchor, shown while the text is still being typed, so the
            // inspector can see where the label will land before committing.
            <circle
              cx={pendingLabel.point[0] * 100}
              cy={pendingLabel.point[1] * 100}
              r={1.2}
              fill={pendingLabel.color}
              opacity={0.9}
            />
          )}
        </svg>
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            background: 'rgba(0,0,0,0.7)',
            padding: '6px 10px',
            borderRadius: 100,
            color: '#fff',
            fontSize: 11,
            fontFamily: T.font,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Ico d={I.sparkle} size={12} fill={color} stroke={color} />
          {TOOL_LABELS[tool]}
        </div>
      </div>

      {pendingLabel && (
        <div
          style={{
            margin: '0 16px 8px',
            padding: 8,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            background: 'rgba(255,255,255,0.08)',
            backdropFilter: 'blur(12px)',
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <input
            value={labelText}
            onChange={(e) => setLabelText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitLabel();
              if (e.key === 'Escape') cancelLabel();
            }}
            maxLength={120}
            autoFocus
            placeholder="Label this point…"
            aria-label="Label text"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#fff',
              fontSize: 14,
              fontFamily: T.font,
            }}
          />
          <Btn tone="light" size="sm" onClick={cancelLabel}>
            Cancel
          </Btn>
          <Btn
            tone="primary"
            size="sm"
            onClick={commitLabel}
            disabled={labelText.trim().length === 0}
            style={{ opacity: labelText.trim().length === 0 ? 0.5 : 1 }}
          >
            Add
          </Btn>
        </div>
      )}

      <div
        style={{
          margin: '0 16px',
          padding: 8,
          background: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(12px)',
          borderRadius: 100,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          border: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        <ToolButton
          icon={I.edit}
          title="Freehand"
          active={tool === 'freehand'}
          onClick={() => setTool('freehand')}
        />
        <ToolButton
          icon={I.circle}
          title="Circle"
          active={tool === 'circle'}
          onClick={() => setTool('circle')}
        />
        <ToolButton
          icon={I.arrow}
          title="Arrow"
          active={tool === 'arrow'}
          onClick={() => setTool('arrow')}
        />
        <ToolButton
          label="T"
          title="Label"
          active={tool === 'label'}
          onClick={() => setTool('label')}
        />
        <button
          type="button"
          onClick={undo}
          disabled={strokes.length === 0 || Boolean(active)}
          aria-label="Undo last stroke"
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            background: 'transparent',
            color: strokes.length ? '#fff' : 'rgba(255,255,255,0.3)',
            border: 'none',
            cursor: strokes.length ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Ico d={I.refresh} size={16} />
        </button>
        <button
          type="button"
          onClick={clear}
          disabled={strokes.length === 0 && !active}
          aria-label="Clear all"
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            background: 'transparent',
            color: strokes.length || active ? '#fff' : 'rgba(255,255,255,0.3)',
            border: 'none',
            cursor: strokes.length || active ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Ico d={I.x} size={16} />
        </button>
        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)' }} />
        <div style={{ display: 'flex', gap: 4 }}>
          {PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              aria-label={`Annotation colour ${c}`}
              aria-pressed={c === color}
              style={{
                width: 18,
                height: 18,
                padding: 0,
                borderRadius: 9,
                background: c,
                border: c === color ? '2px solid #fff' : '1px solid rgba(255,255,255,0.3)',
                opacity: c === color ? 1 : 0.5,
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
      </div>

      <div
        style={{
          margin: '10px 16px 8px',
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
        }}
      >
        <Chip
          tone="hud"
          size="sm"
          icon={<Ico d={I.pin} size={10} />}
        >
          {capture?.gps
            ? `${capture.gps.lat.toFixed(4)}, ${capture.gps.lng.toFixed(4)}`
            : 'No GPS'}
        </Chip>
        <Chip tone="hud" size="sm" icon={<Ico d={I.clock} size={10} />}>
          {capture ? new Date(capture.createdAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }) : '—'}
        </Chip>
      </div>

      <GestureBar dark />
    </div>
  );
}

function pointsToSvg(points: Point[]): string {
  // SVG polyline expects "x,y x,y" in its own viewBox space; here we map
  // normalised 0-1 coordinates into the 0-100 viewBox declared in JSX.
  return points.map(([x, y]) => `${(x * 100).toFixed(2)},${(y * 100).toFixed(2)}`).join(' ');
}

function ToolButton({
  icon,
  label,
  title,
  active,
  onClick,
}: {
  icon?: string | React.ReactNode;
  label?: string;
  title: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={title}
      aria-pressed={active}
      title={title}
      style={{
        width: 36,
        height: 36,
        padding: 0,
        border: 'none',
        borderRadius: 18,
        background: active ? T.brand : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: label && !icon ? 14 : undefined,
        fontWeight: 700,
        fontFamily: T.font,
        cursor: 'pointer',
      }}
    >
      {icon ? <Ico d={icon} size={16} /> : label}
    </button>
  );
}

/**
 * One annotation, in the 0-100 viewBox the surface declares.
 *
 * The viewBox is stretched to the photo with `preserveAspectRatio="none"`, so
 * a circle drawn with a single radius would come out an ellipse of whatever
 * proportion the surface happens to have. Storing and drawing two radii means
 * the shape matches the drag on any screen the photo is later replayed on.
 */
function AnnotationShape({ a, preview }: { a: CaptureAnnotation; preview?: boolean }) {
  const opacity = preview ? 0.9 : 1;
  const stroke = { stroke: a.color, strokeWidth: 0.6, fill: 'none', opacity } as const;

  switch (a.kind) {
    case 'freehand':
      return (
        <polyline
          points={pointsToSvg(a.points)}
          {...stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );

    case 'circle': {
      const [centre, edge] = a.points;
      if (!centre || !edge) return null;
      return (
        <ellipse
          cx={centre[0] * 100}
          cy={centre[1] * 100}
          rx={Math.abs(edge[0] - centre[0]) * 100}
          ry={Math.abs(edge[1] - centre[1]) * 100}
          {...stroke}
        />
      );
    }

    case 'arrow': {
      const [tail, head] = a.points;
      if (!tail || !head) return null;
      const [x1, y1] = [tail[0] * 100, tail[1] * 100];
      const [x2, y2] = [head[0] * 100, head[1] * 100];
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const size = 3.5;
      const spread = Math.PI / 7;
      const wing = (sign: number) =>
        `${(x2 - size * Math.cos(angle + sign * spread)).toFixed(2)},${(
          y2 -
          size * Math.sin(angle + sign * spread)
        ).toFixed(2)}`;
      return (
        <g opacity={opacity}>
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={a.color} strokeWidth={0.6} />
          <polygon points={`${x2},${y2} ${wing(-1)} ${wing(1)}`} fill={a.color} />
        </g>
      );
    }

    case 'label': {
      const [anchor] = a.points;
      if (!anchor || !a.label) return null;
      return (
        <text
          x={anchor[0] * 100}
          y={anchor[1] * 100}
          fill={a.color}
          fontSize={4}
          fontWeight={700}
          dominantBaseline="middle"
          opacity={opacity}
        >
          {a.label}
        </text>
      );
    }
  }
}

/**
 * A drag is a shape only if it travelled. Freehand keeps its own two-point
 * floor; circle and arrow are degenerate at a single point, so a tap that
 * slipped would otherwise leave an invisible zero-size mark on the photo.
 */
function toAnnotation(s: ActiveStroke): CaptureAnnotation | null {
  if (s.kind === 'freehand') {
    return s.points.length >= 2
      ? { id: s.id, kind: 'freehand', points: s.points, color: s.color }
      : null;
  }
  const from = s.points[0];
  const to = s.points[s.points.length - 1];
  if (!from || !to) return null;
  if (Math.hypot(to[0] - from[0], to[1] - from[1]) < MIN_DRAG) return null;
  return { id: s.id, kind: s.kind, points: [from, to], color: s.color };
}
